import {AppConfig, BaseApp, BaseAppProps, BaseAppState} from "shared/BaseApp";
import React, {ReactElement, useState} from 'react';
import {
  Box,
  Button,
  ButtonBase,
  createTheme,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {DEFAULT_THEME_OPTIONS, lightGray} from "shared/colors";
import {FIREBASE_CONFIG} from "./consts";
import {BaseAppPrefs} from "shared/BaseAppPrefs";
import {LoginConfig} from "../shared/Login";
import defaultUserImage from "./res/default_user.png";
import {StyledBoxColumn, StyledBoxRow, StyledSpan} from "../shared/StyledComponents";
import {CARD_SHADOW, DIVIDER, PD_LG, PD_MD, PD_SM, PD_XSM, SZ_SM, SZ_SMSM} from "../shared/dimens";
import peppinStamp from "./res/peppin_logotype.svg";
import {ProvisionedApp} from "../shared/ProvisionedApp";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../shared/BaseFragment";
import {Action, ActionBase, ActionGroup, ActionType} from "../shared/types";
import {Close, ExpandLessOutlined, ExpandMoreOutlined, MenuOutlined} from "@mui/icons-material";

function ActionContentText(props: { action: Action, buttonVariant?: string }): ReactElement {
  return <ButtonBase style={{width: "100%"}}><Typography style={{
    // color: props.buttonVariant === "contained" ? paletteElement.contrastText : paletteElement.main,
    fontWeight: 600,
    textTransform: "none",
  }}>{props.action.text}</Typography>
  </ButtonBase>
}

function getActionContent(action: Action, buttonVariant?: string): ReactElement | string {
  if (action.iconType) {
    const IconType = action.iconType;
    if (action.text) {
      return <StyledBoxRow><IconType/><ActionContentText action={action} buttonVariant={buttonVariant}/></StyledBoxRow>
    } else {
      return <IconType/>
    }
  }
  return <ActionContentText action={action} buttonVariant={buttonVariant}/>;
}

function getActionGroupContent(actionGroup: ActionGroup, buttonVariant?: string): ReactElement | string {
  return <ButtonBase style={{width: "100%"}}><Typography style={{
    // color: props.buttonVariant === "contained" ? paletteElement.contrastText : paletteElement.main,
    fontWeight: 600,
    textTransform: "none",
  }}>{actionGroup.label}</Typography>
  </ButtonBase>;
}

function DrawerActionView(props: { action: Action, indent?: number }) {
  let style = undefined;
  if (props.indent) {
    style = {
      paddingLeft: props.indent * PD_MD,
    };
  }
  return <ListItem disablePadding>
    <ListItemButton onClick={props.action.onClick}>
      <div style={{...style}}>
        {getActionContent(props.action)}
      </div>
    </ListItemButton>
  </ListItem>;
}

function DrawerActionGroupView(props: { actionGroup: ActionGroup, indent?: number }) {
  const [expanded, setExpanded] = useState<boolean>(false);
  return <StyledBoxColumn style={{gap: 0}}>
    <ListItem disablePadding>
      <ListItemButton onClick={(event) => {
        event.stopPropagation();
        setExpanded(!expanded);
      }}>
        {getActionGroupContent(props.actionGroup)}
        {expanded
          ? <ExpandLessOutlined/>
          : <ExpandMoreOutlined/>}
      </ListItemButton>
    </ListItem>
    {expanded
      ? props.actionGroup.actions?.map(actionBase => {
        const indent = (props.indent || 0) + 1;
        switch (actionBase.type) {
          default:
          case ActionType.BUTTON:
            return <DrawerActionView action={actionBase as Action} indent={indent}/>
          case ActionType.GROUP:
            return <DrawerActionGroupView actionGroup={actionBase as ActionGroup} indent={indent}/>;
        }
      })
      : null}
  </StyledBoxColumn>;
}

function PeppinDrawer(props: { actions: ActionBase[] }): ReactElement {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up("md"));

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };
  return <>
    {!md ? <IconButton style={{
        width: SZ_SM,
        height: SZ_SM,
        background: lightGray,
      }} onClick={toggleDrawer(true)}>
        <MenuOutlined style={{color: "black"}}/>
      </IconButton>
      : null}
    <Drawer open={!md && open} anchor="right" onClose={toggleDrawer(false)}>
      <Box sx={{width: 240}} role="presentation" onClick={toggleDrawer(false)}>
        <Box style={{
          display: "flex",
          right: PD_XSM,
          top: PD_XSM,
          alignItems: "center",
          height: SZ_SM,
          position: "absolute",
        }}>
          <IconButton style={{
            width: SZ_SM,
            height: SZ_SM,
            background: lightGray,
          }} onClick={toggleDrawer(false)}>
            <Close style={{color: "black"}}/>
          </IconButton>
        </Box>
        <List style={{paddingTop: SZ_SM + PD_XSM * 2}}>
          {props.actions?.map(actionBase => {
            if (actionBase.type === ActionType.GROUP) {
              const actionGroup = actionBase as ActionGroup;
              return <DrawerActionGroupView actionGroup={actionGroup}/>;
            } else if (!actionBase.type || actionBase.type === ActionType.BUTTON) {
              const action = actionBase as Action;
              return <DrawerActionView action={action}/>;
            }
            return null;
          })}
        </List>
      </Box>
    </Drawer>
  </>;
}

function HeaderToolbar(props: { actions: Action[] }): ReactElement {
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up("md"));
  return <>
    {md
      ? props.actions.map(action =>
        <Button
          variant={action.variant}
          startIcon={action.iconType ? <action.iconType/> : null}
          endIcon={action.dropdown ? <ExpandMoreOutlined/> : null}
          onClick={action.onClick}>
          {action.text}
        </Button>)
      : null}
    <PeppinDrawer actions={props.actions}/>
  </>
}

export type PeppinMainProps = BaseFragmentProps&{}

export type PeppinMainState = BaseFragmentState&{}

export abstract class PeppinMain<P extends PeppinMainProps = PeppinMainProps, S extends PeppinMainState = PeppinMainState> extends BaseFragment<P, S> {

  protected renderHeaderToolbar(): ReactElement {
    return null;
  }

  protected createToolbarActions(): Action[] {
    return null;
  }

  private _headerToolbar: ReactElement;

  renderContainerContent(): React.ReactElement {
    if (this._headerToolbar === undefined) {
      this._headerToolbar = this.renderHeaderToolbar();
      if (!this._headerToolbar) {
        const actions = this.createToolbarActions();
        if (actions?.length > 0) {
          this._headerToolbar = <HeaderToolbar actions={actions}/>
        }
      }
    }
    const headerHeight = 72;
    return <>
      <StyledBoxRow
        style={{
          position: "absolute",
          paddingRight: PD_MD,
          gap: PD_LG,
          width: "100%",
          zIndex: 1,
          boxSizing: "border-box",
          height: headerHeight,
          alignItems: "center",
          borderBottom: DIVIDER
        }}>
        <img src={BaseApp.CONTEXT.getAppConfig().icon} style={{width: 72, height: 72, objectFit: "cover"}}/>
        <Typography variant="h5">{BaseApp.CONTEXT.getAppConfig().name}</Typography>
        <StyledSpan/>
        {this._headerToolbar}
      </StyledBoxRow>
      <div style={{
        display: "flex",
        flexDirection: "column",
        width: "100vw",
        height: "100vh",
        paddingTop: headerHeight,
        overflow: "hidden"
      }}>
        {this.renderContent()}
      </div>
    </>;
  }

  abstract renderContent(): ReactElement;
}

export type AppProps = BaseAppProps & {}

type AppState = BaseAppState & {}

const THEME = createTheme({
  ...DEFAULT_THEME_OPTIONS,
  components: {
    ...DEFAULT_THEME_OPTIONS.components,
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          whiteSpace: "nowrap",
          boxShadow: CARD_SHADOW,
          paddingTop: PD_XSM,
          paddingBottom: PD_XSM,
          paddingLeft: PD_MD,
          paddingRight: PD_MD,
          textTransform: "none",
          borderRadius: SZ_SM,
          // fontWeight: "bold",
        },
      }
    },
  },
  typography: {
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    fontFamily: "Poppins, sans-serif",
    fontSize: 17,
  },
});

class AppPrefs extends BaseAppPrefs {

  private static instance: AppPrefs;

  static getInstance() {
    if (!this.instance) {
      this.instance = new AppPrefs();
    }
    return this.instance;
  }
}

export abstract class PeppinApp extends ProvisionedApp<AppProps, AppState> {

  getAppPrefs<T extends BaseAppPrefs>(): T {
    return AppPrefs.getInstance() as T;
  }

  protected abstract getAppName(): string;

  protected getLoginConfig(): LoginConfig | null | undefined {
    return {
      noLogin: true,
      uiProps: {
        bottomDecorator: this.renderLoginDecorator(),
      },
    };
  }

  private renderLoginDecorator(): ReactElement {
    return <StyledBoxRow style={{padding: PD_SM}}>
      <StyledSpan/>
      <img src={peppinStamp} style={{height: SZ_SMSM, marginBottom: -SZ_SMSM, opacity: 0.33}}/>
      <StyledSpan/>
    </StyledBoxRow>;
  }

  onCreateAppConfig(): AppConfig {
    return {
      name: this.getAppName(),
      icon: "/icon.png",
      logo: "/logo192.png",
      stamp: "/stamp.svg",
      stampText: this.getAppName(),
      defaultUserImage: defaultUserImage,
      theme: THEME,
      privacyUrl: "",
      termsUrl: "",
      loginConfig: this.getLoginConfig(),
      firebaseConfig: {options: FIREBASE_CONFIG},
    };
  }
}